import { webEnv } from "@/environment/environment";

export type DiscoverySurface =
  | "home_most_sale"
  | "home_offer"
  | "home_category"
  | "category_listing"
  | "search"
  | "pdp_related"
  | "blog"
  | "cart_related"
  | "cart_sale"
  | "cart_offer"
  | "cart_recent";

export type DiscoveryEventType = "impression" | "click";

type QueuedEvent = {
  productId: string;
  surface: DiscoverySurface;
  type: DiscoveryEventType;
  position: number;
};

const SESSION_KEY = "nuxino_disc_sid";
const FLUSH_MS = 1500;
const MAX_BATCH = 40;

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const localSeen = new Set<string>();

function getSessionKey(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing && existing.length >= 8) return existing;
    const next =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return `s_${Date.now().toString(36)}`;
  }
}

function localDedupeKey(event: QueuedEvent): string {
  return `${event.surface}:${event.productId}:${event.type}`;
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushQueue();
  }, FLUSH_MS);
}

async function flushQueue() {
  if (!queue.length || typeof window === "undefined") return;
  const batch = queue.splice(0, MAX_BATCH);
  const payload = JSON.stringify({
    sessionKey: getSessionKey(),
    events: batch,
  });
  const url = `${webEnv.api.url}/product-discovery/events`;
  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      credentials: "include",
      keepalive: true,
    });
  } catch {
    // Drop on failure — analytics must not break UX
  }
  if (queue.length) scheduleFlush();
}

export function trackProductDiscovery(event: QueuedEvent) {
  if (typeof window === "undefined") return;
  if (!event.productId || !event.surface) return;

  const key = localDedupeKey(event);
  if (localSeen.has(key)) return;
  localSeen.add(key);

  queue.push({
    productId: event.productId,
    surface: event.surface,
    type: event.type,
    position: Math.min(99, Math.max(0, event.position | 0)),
  });

  if (queue.length >= MAX_BATCH) {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    void flushQueue();
    return;
  }
  scheduleFlush();
}

if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushQueue();
  });
  window.addEventListener("pagehide", () => {
    void flushQueue();
  });
}
